import { useMountEffect } from '@timelyapp/react';
import classNames from 'classnames';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import styles from './useScrollShadows.module.scss';

/**
 * Logic for keeping track of scroll shadows in vertical scrolling containers
 * @returns
 *     scrollRef: ref that must be put on the scrolling element
 *     onScroll: scroll handler for the scrolling element
 *     shadows: rendered shadows, add to parent element
 *      (parent element must be sized the same as scrolling
 *      container, and be relative)
 */
const useScrollShadows = () => {
  const [scrollY, setScrollY] = useState(0);
  const [scrollContainerSize, setScrollContainerSize] = useState(0);
  const [scrollHeight, setScrollHeight] = useState(0);
  const updateScrollData = useCallback((element: HTMLElement) => {
    setScrollY(element.scrollTop);
    setScrollContainerSize(element.offsetHeight);
    setScrollHeight(element.scrollHeight);
  }, []);
  const scrollRef = useRef<HTMLDivElement>(null);
  useMountEffect(() => {
    if (scrollRef.current) {
      updateScrollData(scrollRef.current);
    }
  });
  const onScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      updateScrollData(e.currentTarget);
    },
    [updateScrollData]
  );
  const shadows = useMemo(() => {
    let top = false;
    let bottom = false;
    if (scrollY > 0) {
      top = true;
    }
    if (Math.ceil(scrollY + scrollContainerSize) + 5 < scrollHeight) {
      bottom = true;
    }
    return (
      <>
        {top && <div className={classNames(styles.shadow, styles.top)} />}
        {bottom && <div className={styles.shadow} />}
      </>
    );
  }, [scrollY, scrollContainerSize, scrollHeight]);

  return { scrollRef, onScroll, shadows };
};

export default useScrollShadows;
